import { useEffect, useRef, useState } from "react";
import { Loader2, MailCheck, ArrowLeft, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// Second step of sign-in: the 6-digit code we emailed. Auto-submits when 6 digits are typed.
export function MfaCodeStep({ challenge, onVerified, onBack }) {
  const { verifyMfa, resendMfa } = useAuth();
  const [code, setCode] = useState("");
  const trustDays = challenge.trust_days ?? 0;
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendsLeft, setResendsLeft] = useState(challenge.resends_left ?? 3);
  const [cooldown, setCooldown] = useState(30);
  const [sentNote, setSentNote] = useState("");
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submit = async (value) => {
    const digits = (value ?? code).replace(/\D/g, "");
    if (digits.length !== 6 || busy) return;
    setBusy(true);
    setError("");
    const res = await verifyMfa(challenge.challenge_id, digits, trustDays > 0 && remember);
    setBusy(false);
    if (res.ok) onVerified(res.user);
    else {
      setError(res.error);
      setCode("");
      if (/sign in again/i.test(res.error)) setTimeout(onBack, 1800);
    }
  };

  const onChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(digits);
    if (digits.length === 6) submit(digits);
  };

  const resend = async () => {
    setError("");
    const res = await resendMfa(challenge.challenge_id);
    if (res.ok) {
      setResendsLeft(res.resends_left);
      setCooldown(30);
      setSentNote("A fresh code is on its way.");
    } else setError(res.error);
  };

  return (
    <div data-testid="mfa-step">
      <div className="inline-flex items-center gap-2 text-[#0A8CF2] text-xs font-mono-tech uppercase tracking-widest mb-3">
        <ShieldCheck className="h-3.5 w-3.5" /> Second step
      </div>
      <h1 className="font-heading text-2xl font-extrabold text-white mb-1">Check your email</h1>
      <p className="text-sm text-slate-400 mb-6">
        We sent a 6-digit code to <span className="text-slate-200 font-mono-tech" data-testid="mfa-email-hint">{challenge.email_hint}</span>. It expires in 10 minutes.
      </p>

      <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="space-y-4">
        <div>
          <Label htmlFor="mfa-code" className="text-slate-300 mb-1.5 block">Sign-in code</Label>
          <Input
            id="mfa-code"
            ref={inputRef}
            data-testid="mfa-code-input"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            maxLength={6}
            value={code}
            onChange={onChange}
            placeholder="••••••"
            className="bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100 placeholder:text-slate-600 text-center text-2xl tracking-[0.5em] font-mono-tech h-14"
          />
        </div>
        {trustDays > 0 ? (
          <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
            <input type="checkbox" data-testid="mfa-remember-checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="h-4 w-4 accent-[#0A8CF2]" />
            Trust this device for {trustDays} {trustDays === 1 ? "day" : "days"}
          </label>
        ) : (
          <p className="text-xs text-slate-500" data-testid="mfa-every-time-note">This account is asked for a code on every sign-in.</p>
        )}
        {error && <div data-testid="mfa-error" className="text-sm text-red-400 bg-red-950/40 border border-red-900/50 rounded-md px-3 py-2">{error}</div>}
        {sentNote && !error && <div data-testid="mfa-sent-note" className="text-sm text-emerald-300 flex items-center gap-2"><MailCheck className="h-4 w-4" /> {sentNote}</div>}
        <Button type="submit" data-testid="mfa-verify-btn" disabled={busy || code.length !== 6} className="w-full h-11 bg-[#0A8CF2] hover:bg-[#3AA3FF] text-white font-bold shadow-lg shadow-blue-900/40">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & sign in"}
        </Button>
      </form>

      <div className="flex items-center justify-between mt-5 text-xs text-slate-500">
        <button type="button" onClick={onBack} data-testid="mfa-back-btn" className="inline-flex items-center gap-1 hover:text-white">
          <ArrowLeft className="h-3 w-3" /> Different account
        </button>
        <button type="button" onClick={resend} disabled={cooldown > 0 || resendsLeft <= 0} data-testid="mfa-resend-btn" className="hover:text-white disabled:opacity-50 disabled:hover:text-slate-500">
          {resendsLeft <= 0 ? "No resends left" : cooldown > 0 ? `Resend code in ${cooldown}s` : `Resend code (${resendsLeft} left)`}
        </button>
      </div>
    </div>
  );
}
